/**
 * Dependency imports
 */
import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Box, Stack, Title } from '@mantine/core';
import { useInViewport } from '@mantine/hooks';
import Button from './Button';

/**
 * Root
 */
const Root = styled('div', {
  shouldForwardProp: (prop: PropertyKey) =>
    ![
      'currentIndex',
      'expanded',
      'autoplay',
      'autoplayInterval',
      'inViewport',
    ].includes(prop.toString()),
})<
  Partial<FeatureTabsProps> & {
    currentIndex: number;
    inViewport: boolean;
  }
>(
  ({
    currentIndex = 1,
    expanded,
    autoplay,
    autoplayInterval,
    inViewport,
  }) => css`
    .feature-tab {
      position: relative;

      /* Autoplay progress bar */
      ${autoplay &&
      inViewport &&
      css`
        &:before {
          content: '';
          display: none;
          height: 1px;
          position: absolute;
          top: 0;
          left: 0;
          background: var(--color-primary);
        }

        &:nth-of-type(${currentIndex + 1}) {
          &:before {
            display: block;
            animation: feature-tab-progress-grow ${autoplayInterval}ms linear
              forwards;
          }
        }

        @keyframes feature-tab-progress-grow {
          from {
            width: 0%;
          }

          to {
            width: 100%;
          }
        }
      `}

      /* Title arrow */
      ${!expanded &&
      css`
        > h1 {
          position: relative;

          &:before,
          &:after {
            content: '';
            display: block;
            width: 7px;
            height: 2px;
            border-radius: 99px;
            background-color: #bbb;
            position: absolute;
            top: 50%;
            right: 0;
            transform: translate(0, -50%) rotate(-45deg);
            transition: 300ms transform;
          }

          &:after {
            transform: translate(-55%, -50%) rotate(45deg);
          }
        }

        &:nth-of-type(${currentIndex + 1}) {
          > h1 {
            &:before {
              transform: translate(0, -50%) rotate(45deg);
            }

            &:after {
              transform: translate(-55%, -50%) rotate(-45deg);
            }
          }
        }
      `}
    }

    .feature-tab-content {
      overflow: hidden;
      transition: height 300ms;
      box-sizing: border-box;

      > div {
        padding: 0 0 50px;

        p {
          margin: 0 0 30px;
        }
      }
    }
  `
);

/**
 * FeatureTabs Component
 */
export interface FeatureTabsProps {
  expanded: boolean;
  data: {
    title: string;
    content: React.ReactNode;
    cta?: {
      text: string;
      href: string;
    };
  }[];
  autoplay?: boolean;
  autoplayInterval?: number;
  activeIndex?: number;
  onTabChange?: (index: number) => void;
}

export type FeatureTabsCombinedProps = FeatureTabsProps &
  JSX.IntrinsicElements['div'];

const FeatureTabs: React.FC<FeatureTabsCombinedProps> = ({
  expanded,
  data,
  autoplay = true,
  autoplayInterval = 10000,
  activeIndex = 0,
  onTabChange,
  ...props
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const { inViewport } = useInViewport();

  const [currentIndex, setCurrentIndex] = React.useState(activeIndex);
  const [heightMap, setHeightMap] = React.useState<number[]>([]);

  const updateHeightMap = React.useCallback(() => {
    const heights = data.map((_, index) => {
      const tab = rootRef.current?.querySelectorAll('.feature-tab-content')[index] as HTMLElement;

      if (tab) {
        const currentTabHeightValue = tab.style.height;
        tab.style.height = '';
        const tabHeight = tab.clientHeight;
        tab.style.height = currentTabHeightValue;
        return tabHeight;
      }

      return 0;
    });

    setHeightMap(heights);
  }, [data]);

  React.useEffect(() => {
    updateHeightMap();
    window.addEventListener('resize', updateHeightMap);

    return () => {
      window.removeEventListener('resize', updateHeightMap);
    };
  }, [updateHeightMap]);

  const timeoutRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    if (!expanded && autoplay && inViewport) {
      clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        setCurrentIndex((v) => (v === data.length - 1 ? 0 : v + 1));
      }, autoplayInterval);
    }
  }, [expanded, autoplay, autoplayInterval, currentIndex, data, inViewport]);

  return (
    <Root
      {...props}
      ref={rootRef}
      currentIndex={currentIndex}
      expanded={expanded}
      autoplay={!expanded && autoplay}
      autoplayInterval={autoplayInterval}
      inViewport={inViewport}
    >
      {data.map((tab, index) => {
        return (
          <div key={index} className="feature-tab">
            <Title
              size={24}
              fw={500}
              py={30}
              style={{ cursor: 'pointer', borderTop: '1px solid #52525B22' }}
              onClick={expanded ? undefined : () => setCurrentIndex(index)}
            >
              {tab.title}
            </Title>

            <Box
              className="feature-tab-content"
              style={{
                height: expanded
                  ? 'auto'
                  : currentIndex === index
                    ? heightMap[index]
                    : 0,
              }}
            >
              <Stack>
              <div>{tab.content}</div>
              {tab.cta && (
                <Button
                  href={tab.cta.href}
                  size="small"
                  variant="outline"
                  hasArrow
                  expandOnHover={false}
                >
                  {tab.cta.text}
                </Button>
              )}
              </Stack>
              
            </Box>
          </div>
        );
      })}
    </Root>
  );
};

export default FeatureTabs;